import { SizeInput } from "@/components/shared/SizeInput";
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { z } from 'zod';
import { toast } from 'sonner';
import { ChevronLeft, Save, Loader2, Factory, Monitor, Trash2, Plus } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { trpcClient } from '@/lib/trpc';
import { machineSchema } from '@/lib/schemas';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import StandardDetailLayout from '@/layouts/StandardDetailLayout';
import {
  TanstackFieldWrapper,
  TanstackFormControl,
} from '@/components/ui/tanstack-form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ComboboxConfigMaster } from '@/components/shared/ComboboxConfigMaster';

import { toVnDateString } from "@apv-erp/status";
type FormValues = z.infer<typeof machineSchema>;

const DEFAULT_FORM_VALUES: FormValues = {
  machineId: '',
  name: '',
  type: 'OFFSET_PRINTER',
  processCategory: '',
  imageUrl: '',
  manufacturer: '',
  countryOfOrigin: '',
  yearManufactured: '',
  serialNumber: '',
  workingFormat: '',
  plateSize: '',
  paperWeightRange: '',
  powerConsumptionKw: null,
  technicalModel: '',
  technicalSpecs: {},
  standardProductivity: null,
  setupTimeMinutes: null,
  hourlyRate: null,
  costPerHour: null,
  status: 'IDLE',
  totalRunningHours: null,
  totalOutputQuantity: null,
  maintenanceCycleHours: null,
  maintenanceCycleDays: null,
  nextMaintenanceDate: '',
  nextMaintenanceHours: null,
};

function mapMachineToFormValues(machine: Record<string, unknown>): FormValues {
  const mapped: Partial<FormValues> = {};
  for (const key of Object.keys(DEFAULT_FORM_VALUES) as Array<keyof FormValues>) {
    if (machine[key] !== undefined && machine[key] !== null) {
      if (key === 'nextMaintenanceDate' && machine[key]) {
         mapped[key] = toVnDateString(machine[key] as string);
      } else {
         mapped[key] = machine[key] as any;
      }
    } else {
      mapped[key] = DEFAULT_FORM_VALUES[key] as any;
    }
  }
  return mapped as FormValues;
}

export function MachineForm({
  isModal = false,
  onCancel,
  onSuccess,
  machineId: propsMachineId,
  initialData,
  submitLabel,
}: {
  isModal?: boolean;
  onCancel?: () => void;
  onSuccess?: (data: any) => void;
  machineId?: string;
  initialData?: any;
  submitLabel?: string;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = useParams({ strict: false });
  const machineId = propsMachineId || (params as any).machineId || (params as any).id;
  const isEditing = !!machineId;

  const [specs, setSpecs] = useState<{ key: string; value: string }[]>([]);

  const { data: machine, isLoading: isFetching } = useQuery({
    queryKey: ['machine', machineId],
    queryFn: () => trpcClient.machines.getById.query({ id: machineId! }),
    enabled: isEditing,
  });

  useEffect(() => {
    if (machine?.technicalSpecs) {
      try {
        const obj = typeof machine.technicalSpecs === 'string' 
          ? JSON.parse(machine.technicalSpecs) 
          : machine.technicalSpecs;
        setSpecs(Object.entries(obj).map(([key, value]) => ({ key, value: String(value) })));
      } catch (e) {
        console.error("Failed to parse technicalSpecs", e);
      }
    } else if (machine?.specs) {
      // Fallback cho schema cũ nếu còn sót
      try {
        const obj = typeof machine.specs === 'string' 
          ? JSON.parse(machine.specs) 
          : machine.specs;
        setSpecs(Object.entries(obj).map(([key, value]) => ({ key, value: String(value) })));
      } catch (e) {}
    }
  }, [machine]);

  const createMutation = useMutation({
    mutationFn: (data: FormValues) => trpcClient.machines.create.mutate(data),
    onSuccess: (res: any) => {
      toast.success('Thêm thiết bị thành công');
      queryClient.invalidateQueries({ queryKey: ['machines'] });
      queryClient.invalidateQueries({ queryKey: ['combobox'] });
      if (onSuccess) {
        onSuccess({
          id: res.id,
          label: res.machineId ? `${res.name} (${res.machineId})` : res.name,
          original: res,
        });
      } else {
        navigate({ to: '/machines' });
      }
    },
    onError: (error) => {
      toast.error(`Lỗi: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: FormValues) =>
      trpcClient.machines.update.mutate({ id: machineId!, data }),
    onSuccess: (res: any) => {
      toast.success('Cập nhật thiết bị thành công');
      queryClient.invalidateQueries({ queryKey: ['machines'] });
      queryClient.invalidateQueries({ queryKey: ['machine', machineId] });
      queryClient.invalidateQueries({ queryKey: ['combobox'] });
      if (onSuccess) {
        onSuccess({
          id: res?.id || machineId,
          label: res?.machineId ? `${res.name} (${res.machineId})` : (res?.name || ''),
          original: res,
        });
      } else {
        navigate({ to: '/machines' });
      }
    },
    onError: (error) => {
      toast.error(`Lỗi: ${error.message}`);
    },
  });

  const initialMergedValues: FormValues = isEditing && machine
    ? mapMachineToFormValues(machine)
    : {
        ...DEFAULT_FORM_VALUES,
        ...(initialData ? mapMachineToFormValues({ ...DEFAULT_FORM_VALUES, ...initialData }) : {}),
        machineId: initialData?.machineId || (initialData?.name ? `MAY-${Date.now().toString().slice(-4)}` : ''),
        name: initialData?.name || '',
      };

  const form = useForm({
    defaultValues: initialMergedValues,
    onSubmit: async ({ value }) => {
      const technicalSpecs = specs.reduce((acc, curr) => {
        if (curr.key.trim()) acc[curr.key.trim()] = curr.value;
        return acc;
      }, {} as Record<string, any>);
      
      const payload = { ...value, technicalSpecs };
      
      // Clean up empty dates
      if (!payload.nextMaintenanceDate) payload.nextMaintenanceDate = null;
      
      if (isEditing) {
        await updateMutation.mutateAsync(payload);
      } else {
        await createMutation.mutateAsync(payload);
      }
    },
  });

  useEffect(() => {
    if (isEditing && machine) {
      form.reset(mapMachineToFormValues(machine));
    } else if (!isEditing && initialData?.name) {
      form.setFieldValue('name', initialData.name);
      if (!form.getFieldValue('machineId')) {
        form.setFieldValue('machineId', initialData.machineId || `MAY-${Date.now().toString().slice(-4)}`);
      }
    }
  }, [isEditing, machine, initialData, form]);

  if (isEditing && isFetching) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const formContent = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* 1. ẢNH VÀ THÔNG TIN CHUNG */}
      <Card className="md:col-span-2 shadow-sm border-slate-200">
        <CardHeader className="bg-slate-50/50 border-b pb-4">
          <CardTitle className="text-base font-semibold flex items-center text-slate-800">
            <Monitor className="w-4 h-4 mr-2 text-indigo-500" />
            1. Thông Tin Nhận Diện
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            <form.Field
              name="machineId"
              validators={{ onChange: machineSchema.shape.machineId }}
              children={(field) => (
                <TanstackFieldWrapper field={field} label="Mã Máy" required>
                  <TanstackFormControl>
                    <Input
                      placeholder="VD: MAY-IN-01"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            />

            <form.Field
              name="name"
              validators={{ onChange: machineSchema.shape.name }}
              children={(field) => (
                <TanstackFieldWrapper field={field} label="Tên Máy" required>
                  <TanstackFormControl>
                    <Input
                      placeholder="Máy in Offset 4 màu"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            />

            <form.Field
              name="type"
              children={(field) => (
                <TanstackFieldWrapper field={field} label="Phân Loại Nhóm">
                  <TanstackFormControl>
                    <ComboboxConfigMaster
                      categoryCode="machine_type"
                      value={field.state.value || ''}
                      onChange={field.handleChange}
                      placeholder="Chọn loại máy"
                      hideLabel
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            />

            <form.Field
              name="processCategory"
              children={(field) => (
                <TanstackFieldWrapper field={field} label="Danh Mục Công Đoạn">
                  <TanstackFormControl>
                    <ComboboxConfigMaster
                      categoryCode="PROCESS_STAGE"
                      valueField="name"
                      value={field.state.value || ''}
                      onChange={field.handleChange}
                      placeholder="VD: In ấn, Thành phẩm..."
                      hideLabel
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            />
            
            <form.Field
              name="manufacturer"
              children={(field) => (
                <TanstackFieldWrapper field={field} label="Hãng Sản Xuất">
                  <TanstackFormControl>
                    <Input
                      placeholder="Heidelberg, Komori..."
                      value={field.state.value || ''}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            />

            <form.Field
              name="yearManufactured"
              children={(field) => (
                <TanstackFieldWrapper field={field} label="Năm Sản Xuất">
                  <TanstackFormControl>
                    <Input
                      placeholder="2020"
                      value={field.state.value || ''}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            />
            
            <form.Field
              name="serialNumber"
              children={(field) => (
                <TanstackFieldWrapper field={field} label="Serial Number">
                  <TanstackFormControl>
                    <Input
                      placeholder="S/N..."
                      value={field.state.value || ''}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            />
            
            <form.Field
              name="imageUrl"
              children={(field) => (
                <TanstackFieldWrapper field={field} label="Link Ảnh (URL)">
                  <TanstackFormControl>
                    <Input
                      placeholder="https://..."
                      value={field.state.value || ''}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            />
          </div>
        </CardContent>
      </Card>

      {/* 2. NĂNG LỰC KỸ THUẬT */}
      <Card className="shadow-sm border-slate-200">
        <CardHeader className="bg-slate-50/50 border-b pb-4">
          <CardTitle className="text-base font-semibold flex items-center text-slate-800">
            <Factory className="w-4 h-4 mr-2 text-emerald-500" />
            2. Năng Lực Kỹ Thuật
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <form.Field
                name="workingFormat"
                children={(field) => (
                  <TanstackFieldWrapper field={field} label="Khổ Làm Việc">
                    <SizeInput value={field.state.value || ''} onChange={(v) => field.handleChange(v)} onBlur={field.handleBlur} testId="size-workingFormat" />
                  </TanstackFieldWrapper>
                )}
              />
              <form.Field
                name="plateSize"
                children={(field) => (
                  <TanstackFieldWrapper field={field} label="Khổ Kẽm CTP (máy in)">
                    <SizeInput value={field.state.value || ''} onChange={(v) => field.handleChange(v)} onBlur={field.handleBlur} testId="machine-plate-size" />
                  </TanstackFieldWrapper>
                )}
              />
              <form.Field
                name="paperWeightRange"
                children={(field) => (
                  <TanstackFieldWrapper field={field} label="Định Lượng Giấy">
                    <TanstackFormControl>
                      <Input
                        placeholder="VD: 80 - 450 gsm"
                        value={field.state.value || ''}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                      />
                    </TanstackFormControl>
                  </TanstackFieldWrapper>
                )}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <form.Field
                name="standardProductivity"
                children={(field) => (
                  <TanstackFieldWrapper field={field} label="Sản Lượng Chuẩn (/h)">
                    <TanstackFormControl>
                      <Input
                        type="number"
                        value={field.state.value || ''}
                        onChange={(e) => field.handleChange(e.target.value ? Number(e.target.value) : null)}
                        onBlur={field.handleBlur}
                      />
                    </TanstackFormControl>
                  </TanstackFieldWrapper>
                )}
              />
              <form.Field
                name="setupTimeMinutes"
                children={(field) => (
                  <TanstackFieldWrapper field={field} label="TG Setup (Phút)">
                    <TanstackFormControl>
                      <Input
                        type="number"
                        value={field.state.value || ''}
                        onChange={(e) => field.handleChange(e.target.value ? Number(e.target.value) : null)}
                        onBlur={field.handleBlur}
                      />
                    </TanstackFormControl>
                  </TanstackFieldWrapper>
                )}
              />
            </div>
            
            <form.Field
              name="powerConsumptionKw"
              children={(field) => (
                <TanstackFieldWrapper field={field} label="Công Suất (kW)">
                  <TanstackFormControl>
                    <Input
                      type="number"
                      step="0.1"
                      value={field.state.value || ''}
                      onChange={(e) => field.handleChange(e.target.value ? Number(e.target.value) : null)}
                      onBlur={field.handleBlur}
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            />
            
            {/* Dynamic Specs */}
            <div className="mt-4 pt-4 border-t">
               <label className="block text-sm font-medium text-slate-700 mb-3">Thông số kỹ thuật mở rộng</label>
               <div className="space-y-2">
                 {specs.map((spec, index) => (
                   <div key={index} className="flex items-center gap-2">
                     <Input 
                       placeholder="Thuộc tính (VD: Số màu)" 
                       value={spec.key} 
                       className="w-1/3"
                       onChange={(e) => {
                         const newSpecs = [...specs];
                         newSpecs[index].key = e.target.value;
                         setSpecs(newSpecs);
                       }} 
                     />
                     <Input 
                       placeholder="Giá trị (VD: 4)" 
                       value={spec.value} 
                       className="flex-1"
                       onChange={(e) => {
                         const newSpecs = [...specs];
                         newSpecs[index].value = e.target.value;
                         setSpecs(newSpecs);
                       }} 
                     />
                     <Button 
                       type="button" 
                       variant="ghost" 
                       size="icon" 
                       className="text-rose-500 hover:text-rose-600 hover:bg-rose-50 shrink-0"
                       onClick={() => {
                         const newSpecs = [...specs];
                         newSpecs.splice(index, 1);
                         setSpecs(newSpecs);
                       }}
                     >
                       <Trash2 className="w-4 h-4" />
                     </Button>
                   </div>
                 ))}
                 <Button 
                   type="button" 
                   variant="outline" 
                   size="sm" 
                   className="w-full mt-2 border-dashed border-slate-300 text-slate-600"
                   onClick={() => setSpecs([...specs, { key: '', value: '' }])}
                 >
                   <Plus className="w-4 h-4 mr-2" /> Thêm Thông Số
                 </Button>
               </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 3. VẬN HÀNH & BẢO TRÌ */}
      <Card className="shadow-sm border-slate-200">
        <CardHeader className="bg-slate-50/50 border-b pb-4">
          <CardTitle className="text-base font-semibold flex items-center text-slate-800">
            <Monitor className="w-4 h-4 mr-2 text-amber-500" />
            3. Quản Trị Vận Hành (CMMS)
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <form.Field
                name="maintenanceCycleHours"
                children={(field) => (
                  <TanstackFieldWrapper field={field} label="Chu Kỳ Bảo Trì (Giờ)">
                    <TanstackFormControl>
                      <Input
                        type="number"
                        value={field.state.value || ''}
                        onChange={(e) => field.handleChange(e.target.value ? Number(e.target.value) : null)}
                        onBlur={field.handleBlur}
                      />
                    </TanstackFormControl>
                  </TanstackFieldWrapper>
                )}
              />
              <form.Field
                name="maintenanceCycleDays"
                children={(field) => (
                  <TanstackFieldWrapper field={field} label="Chu Kỳ Bảo Trì (Ngày)">
                    <TanstackFormControl>
                      <Input
                        type="number"
                        value={field.state.value || ''}
                        onChange={(e) => field.handleChange(e.target.value ? Number(e.target.value) : null)}
                        onBlur={field.handleBlur}
                      />
                    </TanstackFormControl>
                  </TanstackFieldWrapper>
                )}
              />
            </div>
            
            <form.Field
              name="nextMaintenanceDate"
              children={(field) => (
                <TanstackFieldWrapper field={field} label="Ngày Bảo Trì Dự Kiến">
                  <TanstackFormControl>
                    <Input
                      type="date"
                      value={field.state.value || ''}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            />
            
            <div className="grid grid-cols-2 gap-4 mt-2">
              <form.Field
                name="hourlyRate"
                children={(field) => (
                  <TanstackFieldWrapper field={field} label="Đơn Giá Thuê (VNĐ/h)">
                    <TanstackFormControl>
                      <Input
                        type="number"
                        value={field.state.value || ''}
                        onChange={(e) => field.handleChange(e.target.value ? Number(e.target.value) : null)}
                        onBlur={field.handleBlur}
                      />
                    </TanstackFormControl>
                  </TanstackFieldWrapper>
                )}
              />
              <form.Field
                name="costPerHour"
                children={(field) => (
                  <TanstackFieldWrapper field={field} label="Khấu Hao (VNĐ/h)">
                    <TanstackFormControl>
                      <Input
                        type="number"
                        value={field.state.value || ''}
                        onChange={(e) => field.handleChange(e.target.value ? Number(e.target.value) : null)}
                        onBlur={field.handleBlur}
                      />
                    </TanstackFormControl>
                  </TanstackFieldWrapper>
                )}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  if (isModal) {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
        className="space-y-4"
      >
        {formContent}
        <div className="flex justify-end gap-2 pt-4 border-t mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSaving}
          >
            Hủy
          </Button>
          <Button type="submit" disabled={isSaving} className="bg-indigo-600 hover:bg-indigo-700">
            {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            <Save className="mr-2 h-4 w-4" />
            {submitLabel || (isEditing ? 'Lưu Thiết Bị' : 'Tạo Mới Thiết Bị')}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <StandardDetailLayout
      breadcrumbs={[
        { label: 'Thiết Bị & Máy Móc', to: '/machines' },
        { label: isEditing ? (machine?.name || machineId || 'Chỉnh sửa') : 'Thêm mới' },
      ]}
      title={isEditing ? `Sửa ${machine?.machineId || machineId}` : 'Tạo Mới Thiết Bị'}
      onBack={() => {
        if (onCancel) onCancel();
        else navigate({ to: '/machines' });
      }}
      showContextPanel={false}
      actions={
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (onCancel) onCancel();
              else navigate({ to: '/machines' });
            }}
            disabled={isSaving}
          >
            Hủy
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={isSaving}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            onClick={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}
          >
            {isSaving ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-1.5 h-4 w-4" />
            )}
            {submitLabel || (isEditing ? 'Lưu Thiết Bị' : 'Tạo Mới Thiết Bị')}
          </Button>
        </div>
      }
    >
      <div className="container mx-auto p-4 md:p-6 space-y-6 max-w-5xl">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
          className="space-y-6"
        >
          {formContent}
          <div className="flex justify-end space-x-2 pt-4 border-t border-slate-200">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (onCancel) onCancel();
                else navigate({ to: '/machines' });
              }}
              disabled={isSaving}
            >
              Hủy
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {isSaving ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-1.5 h-4 w-4" />
              )}
              {submitLabel || (isEditing ? 'Lưu Thiết Bị' : 'Tạo Mới Thiết Bị')}
            </Button>
          </div>
        </form>
      </div>
    </StandardDetailLayout>
  );
}

export default MachineForm;
